import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { SessionTimeoutService } from '../services/session-timeout.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private authService?: AuthService;
  private sessionTimeout?: SessionTimeoutService;
  private readonly ACTIVE_SITE_KEY = 'pos_active_site_id';

  constructor(private injector: Injector) {}

  private getAuthService(): AuthService {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    return this.authService;
  }

  private getSessionTimeout(): SessionTimeoutService {
    if (!this.sessionTimeout) {
      this.sessionTimeout = this.injector.get(SessionTimeoutService);
    }
    return this.sessionTimeout;
  }

  private getActiveSiteId(): string | null {
    return localStorage.getItem(this.ACTIVE_SITE_KEY);
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Add auth token to requests
    const authToken = this.getAuthService().getToken();

    if (authToken && !this.isAuthUrl(request.url)) {
      request = this.addTokenToRequest(request, authToken);
      this.getSessionTimeout().touch('http');
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          !this.isAuthUrl(request.url)
        ) {
          return this.handle401Error(request, next, error);
        }
        return throwError(() => error);
      }),
    );
  }

  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    const headers: { [key: string]: string } = {
      Authorization: `Bearer ${token}`,
    };

    // Add siteId and tenantId as headers
    if (!this.isAuthUrl(request.url)) {
      try {
        const activeSiteId = this.getActiveSiteId();
        const currentUser = this.getAuthService()?.getCurrentUserValue();
        const tenantId = currentUser?.tenant?.id;

        if (
          activeSiteId &&
          typeof activeSiteId === 'string' &&
          activeSiteId.length > 0
        ) {
          headers['X-Site-Id'] = activeSiteId;
        }

        if (tenantId && typeof tenantId === 'string' && tenantId.length > 0) {
          headers['X-Tenant-Id'] = tenantId;
        }
      } catch (error) {
        console.debug('Could not add site/tenant headers:', error);
      }
    }

    return request.clone({
      setHeaders: headers,
    });
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.getAuthService().getRefreshToken();

      if (!refreshToken) {
        this.isRefreshing = false;
        this.getAuthService().forceLogout();
        return throwError(() => originalError);
      }

      return this.getAuthService()
        .refreshToken()
        .pipe(
          switchMap((tokens) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(tokens.accessToken);
            this.getSessionTimeout().touch('http');
            return next.handle(
              this.addTokenToRequest(request, tokens.accessToken),
            );
          }),
          catchError((error) => {
            this.isRefreshing = false;
            this.getAuthService().forceLogout();
            return throwError(() => error);
          }),
        );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        const accessToken = token as string;
        this.getSessionTimeout().touch('http');
        return next.handle(this.addTokenToRequest(request, accessToken));
      }),
      catchError((error) => {
        this.getAuthService().forceLogout();
        return throwError(() => error);
      }),
    );
  }

  private isAuthUrl(url: string): boolean {
    return (
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/google') ||
      url.includes('/auth/password-reset')
    );
  }
}
